import { useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "../../../lib/analytics";
import { api } from "../../../lib/api";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { OnboardingAnswers, Question } from "../../../types/onboarding";
import styles from "./SelectProviderQuestion.module.css";

interface SearchResult {
	id: string;
	name: string;
	address: string;
	rating?: number;
}

interface SelectProviderQuestionProps {
	question: Question;
	answers: OnboardingAnswers;
	onInputChange: (field: string, value: string | number | string[]) => void;
}

export function SelectProviderQuestion({
	question,
	answers,
	onInputChange,
}: SelectProviderQuestionProps) {
	const { t, isRTL } = useClientTranslations("onboarding");
	const { logEvent } = useAnalytics();
	const [selectedOption, setSelectedOption] = useState<boolean | null>(
		answers.hasClinicProvider !== undefined
			? answers.hasClinicProvider === "true" ||
					answers.hasClinicProvider === true
			: null,
	);
	const [clinicName, setClinicName] = useState(
		(answers.providerName as string) || "",
	);
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [showResults, setShowResults] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (answers.hasClinicProvider !== undefined) {
			const hasProvider =
				answers.hasClinicProvider === "true" ||
				answers.hasClinicProvider === true;
			setSelectedOption(hasProvider);
		}
		if (answers.providerName) {
			setClinicName(answers.providerName as string);
		}
	}, [answers.hasClinicProvider, answers.providerName]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setShowResults(false);
			}
		};

		if (showResults) {
			document.addEventListener("mousedown", handleClickOutside);
			return () =>
				document.removeEventListener("mousedown", handleClickOutside);
		}
	}, [showResults]);

	// Log when user finishes typing a custom clinic name
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (clinicName.trim() && selectedOption === true && !showResults) {
				logEvent("clinic_custom_name_entered", undefined, {
					clinic_name: clinicName,
					field_name: question.field,
					question_type: question.type,
					name_length: clinicName.length.toString(),
				});
			}
		}, 2000);

		return () => clearTimeout(timeoutId);
	}, [clinicName, selectedOption, showResults, logEvent, question]);

	const handleOptionSelect = (value: boolean) => {
		setSelectedOption(value);
		onInputChange("hasClinicProvider", value.toString());

		logEvent("clinic_provider_option_selected", undefined, {
			has_clinic_provider: value.toString(),
			field_name: question.field,
			question_type: question.type,
		});

		// If user selects "No", clear the provider name
		if (!value) {
			setClinicName("");
			onInputChange("providerName", "");
		}
	};

	// Debounced search function
	const debouncedSearch = useCallback(
		(query: string) => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(async () => {
				if (query.trim().length >= 2) {
					setIsSearching(true);
					try {
						const response = await api.get<{
							results: SearchResult[];
							hasResults: boolean;
						}>(`/api/search/clinics?query=${encodeURIComponent(query)}`);
						setSearchResults(response.data.results || []);

						logEvent("clinic_search_results_received", undefined, {
							search_query: query,
							results_count: (response.data.results || []).length.toString(),
							field_name: question.field,
							question_type: question.type,
						});
					} catch (error) {
						console.error("Search error:", error);
						setSearchResults([]);

						logEvent("clinic_search_error", undefined, {
							search_query: query,
							error_message: error instanceof Error ? error.message : "unknown",
							field_name: question.field,
							question_type: question.type,
						});
					} finally {
						setIsSearching(false);
					}
				} else {
					setSearchResults([]);
				}
			}, 300);
		},
		[logEvent, question],
	);

	const handleClinicNameChange = (value: string) => {
		setClinicName(value);
		onInputChange("providerName", value);

		// Log when user starts typing
		if (value.trim().length === 1) {
			logEvent("clinic_search_started", undefined, {
				field_name: question.field,
				question_type: question.type,
			});
		}

		// Trigger search
		debouncedSearch(value);
	};

	const handleSelectResult = (result: SearchResult) => {
		setClinicName(result.name);
		onInputChange("providerName", result.name);
		setSearchResults([]);
		setShowResults(false);

		logEvent("clinic_selected_from_search", undefined, {
			clinic_name: result.name,
			clinic_id: result.id,
			field_name: question.field,
			question_type: question.type,
			search_results_count: searchResults.length.toString(),
		});
	};

	return (
		<div className={styles.container} dir={isRTL ? "rtl" : "ltr"}>
			{/* Radio Options */}
			<div className={styles.radioGroup}>
				{/* Yes Option */}
				<button
					type="button"
					onClick={() => handleOptionSelect(true)}
					className={`${styles.radioOption} ${
						selectedOption === true ? styles.radioOptionSelected : ""
					}`}
				>
					<div
						className={`${styles.radioButton} ${
							selectedOption === true ? styles.radioButtonSelected : ""
						}`}
					>
						{selectedOption === true && (
							<div className={styles.radioButtonDot} />
						)}
					</div>
					<span className={styles.radioLabel}>
						{t("questions.clinicProvider.options.yes")}
					</span>
				</button>

				{/* No Option */}
				<button
					type="button"
					onClick={() => handleOptionSelect(false)}
					className={`${styles.radioOption} ${
						selectedOption === false ? styles.radioOptionSelected : ""
					}`}
				>
					<div
						className={`${styles.radioButton} ${
							selectedOption === false ? styles.radioButtonSelected : ""
						}`}
					>
						{selectedOption === false && (
							<div className={styles.radioButtonDot} />
						)}
					</div>
					<span className={styles.radioLabel}>
						{t("questions.clinicProvider.options.no")}
					</span>
				</button>
			</div>

			{/* Clinic dropdown for Yes option */}
			{selectedOption === true && (
				<div className={styles.clinicSearchSection}>
					<label className={styles.clinicLabel} htmlFor="clinicName">
						{t("questions.clinicProvider.selectClinic")}
					</label>
					<div className={styles.searchContainer} ref={dropdownRef}>
						{/* Collapsed State */}
						<button
							type="button"
							onClick={() => setShowResults(!showResults)}
							className={`${styles.searchTrigger} ${
								showResults ? styles.searchTriggerOpen : ""
							}`}
						>
							<span
								className={
									clinicName ? styles.searchText : styles.searchPlaceholder
								}
							>
								{clinicName ||
									t("questions.clinicProvider.selectClinicPlaceholder")}
							</span>
							<svg
								className={`${styles.chevron} ${
									showResults ? styles.chevronOpen : ""
								}`}
								width="14"
								height="8"
								viewBox="0 0 14 8"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M1 1L7 7L13 1"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>

						{/* Expanded Dropdown */}
						{showResults && (
							<div className={styles.searchDropdown}>
								{/* Search Input Header */}
								<div className={styles.searchHeader}>
									<div className={styles.searchInputWrapper}>
										<input
											type="text"
											placeholder={t("questions.clinicProvider.searchClinics")}
											value={clinicName}
											onChange={(e) => handleClinicNameChange(e.target.value)}
											className={styles.searchInput}
										/>
										<svg
											className={styles.searchIcon}
											width="14"
											height="14"
											viewBox="0 0 14 14"
											fill="none"
											aria-hidden="true"
										>
											<circle
												cx="6"
												cy="6"
												r="5"
												stroke="#aaaaaa"
												strokeWidth="1.5"
											/>
											<path
												d="M10 10L13 13"
												stroke="#aaaaaa"
												strokeWidth="1.5"
												strokeLinecap="round"
											/>
										</svg>
									</div>
								</div>

								{/* Search Results */}
								<div className={styles.searchResultsList}>
									{isSearching ? (
										<div className={styles.searchStatus}>
											{t("questions.clinicProvider.searching")}
										</div>
									) : searchResults.length > 0 ? (
										<div className={styles.resultItems}>
											{searchResults.map((result) => (
												<button
													key={result.id}
													type="button"
													onClick={() => handleSelectResult(result)}
													className={styles.resultItem}
												>
													<div className={styles.resultRadio} />
													<div className={styles.resultContent}>
														<div className={styles.resultName}>
															{result.name}
														</div>
														<div className={styles.resultAddress}>
															{result.address}
														</div>
													</div>
												</button>
											))}
										</div>
									) : clinicName.trim().length >= 2 ? (
										<div className={styles.addNewSection}>
											<button
												type="button"
												onClick={() => {
													setShowResults(false);
												}}
												className={styles.addNewButton}
											>
												<span>
													{t("questions.clinicProvider.addNewClinic")}
												</span>
												<svg
													width="16"
													height="16"
													viewBox="0 0 16 16"
													fill="none"
													aria-hidden="true"
												>
													<path
														d="M8 3V13M3 8H13"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
													/>
												</svg>
											</button>
										</div>
									) : (
										<div className={styles.searchStatus}>
											{t("questions.clinicProvider.startTyping")}
										</div>
									)}
								</div>
							</div>
						)}
					</div>
					<p className={styles.helpText}>
						{t("questions.clinicProvider.helpText")}
					</p>
				</div>
			)}

			{/* No option help text */}
			{selectedOption === false && (
				<div className={styles.noOptionSection}>
					<p className={styles.noOptionText}>
						{t("questions.clinicProvider.noHelpText")}
					</p>
				</div>
			)}
		</div>
	);
}
